import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import {
  ARTIFACT_TYPES,
  ARTIFACT_TYPE_LABELS,
  type ArtifactType,
  type SubmissionView,
} from '@thottbot/shared';
import { ApiError, apiSend } from '../api/client';
import { LoginGate } from '../components/LoginGate';
import { ArtifactForm, type ArtifactFormSubmit } from '../features/artifact/ArtifactForm';

function isType(t: string | null): t is ArtifactType {
  return !!t && (ARTIFACT_TYPES as readonly string[]).includes(t);
}

export function Submit() {
  const [params, setParams] = useSearchParams();
  const initialType = params.get('type');
  const [type, setType] = useState<ArtifactType>(isType(initialType) ? initialType : 'quest');
  const [done, setDone] = useState<SubmissionView | null>(null);
  const mutation = useMutation({
    mutationFn: (v: ArtifactFormSubmit) =>
      apiSend<SubmissionView>('POST', '/api/submissions', {
        kind: 'create',
        type,
        ...v,
        note: v.note || undefined,
      }),
    onSuccess: (s) => setDone(s),
  });

  return (
    <div className="page">
      <title>Submit</title>
      <h1>Submit a new entry</h1>
      <LoginGate action="submit an entry">
        {done ? (
          <div className="notice" role="status">
            <b>Submitted for review.</b> <i>{done.proposedName}</i> is in the moderation queue; you
            can follow it on <Link to="/me">your contributions page</Link>.{' '}
            <button type="button" onClick={() => setDone(null)}>
              Submit another
            </button>
          </div>
        ) : (
          <>
            <div className="form-row">
              <label htmlFor="f-type">Type</label>
              <select
                id="f-type"
                value={type}
                onChange={(e) => {
                  setType(e.target.value as ArtifactType);
                  setParams({ type: e.target.value });
                }}
              >
                {ARTIFACT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ARTIFACT_TYPE_LABELS[t].singular}
                  </option>
                ))}
              </select>
            </div>
            <ArtifactForm
              key={type}
              type={type}
              mode="create"
              busy={mutation.isPending}
              serverError={mutation.error ? describeError(mutation.error) : null}
              onSubmit={(v) => mutation.mutate(v)}
            />
          </>
        )}
      </LoginGate>
    </div>
  );
}

export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Slow down: you have hit the submission limit for now.';
    if (err.status === 403) return 'Your account cannot do that.';
    return err.message || `Request failed (${err.status})`;
  }
  return 'Request failed.';
}
